import { useCallback } from "react";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { useCanCancelOrganizationInvite } from "@hooks/permissions/organization/useCanCancelOrganizationInvite";
import {
    useResendOrganizationInviteMutation,
    useCancelOrganizationInviteMutation,
} from "@api/organizationMember.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { withCurrentOrigin } from "@lib/currentOriginLink";
import { InviteCode } from "@common/types/inviteCode";
import { OrganizationMemberWorkspaceAssignment } from "@common/dto/organization/organization-member.dto";
import { Menu } from "@design-system/Menu/Menu.lib";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";

interface InviteActionsCellProps {
    invite: InviteCode & {
        type: "invite";
        inviteAssignments: OrganizationMemberWorkspaceAssignment[];
    };
}

export const InviteActionsCell = ({ invite }: InviteActionsCellProps) => {
    const activeOrganization = useActiveOrganization();
    const { toast } = useToaster();

    const copyToClipboard = useCopyToClipboard({
        successMessage: "Link copied to clipboard",
    });

    const canCancelInvite = useCanCancelOrganizationInvite(invite);
    const canResendInvite = useCanPerformOrganizationAction(
        OrganizationAction.RESEND_INVITE,
    );
    const resendInvite = useResendOrganizationInviteMutation(
        activeOrganization!,
    );
    const cancelInvite = useCancelOrganizationInviteMutation(
        activeOrganization!,
    );

    const handleCancel = useCallback(() => {
        cancelInvite.mutate(invite);
        toast("Invite canceled");
    }, [cancelInvite, invite, toast]);

    const handleResend = useCallback(() => {
        resendInvite.mutate(invite);
        toast("Invite resent");
    }, [invite, resendInvite, toast]);

    const handleCopyInviteLink = useCallback(() => {
        copyToClipboard(withCurrentOrigin(invite.inviteLink));
    }, [copyToClipboard, invite.inviteLink]);

    return (
        <Menu.More className={tableStyles.showOnHover}>
            <Menu.Item onClick={handleCopyInviteLink}>
                Copy invite link
            </Menu.Item>

            {canResendInvite && (
                <Menu.Item onClick={handleResend}>Resend invite</Menu.Item>
            )}

            {canCancelInvite && (
                <Menu.Item destructive onClick={handleCancel}>
                    Cancel invite
                </Menu.Item>
            )}
        </Menu.More>
    );
};
